import React, { useState, useEffect } from 'react';
import patientService from '../../../../../services/patient.service';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import Loading from '../../../../common/Loading';
import { toast } from 'react-toastify';

const PatientAppointments = ({ patient }) => {
  const [loading, setLoading] = useState(false);
  const clinicId = useEyeconxStore((state) => state.user.clinicId);
  const [appointmentsData, setAppointmentsData] = useState(null);

  useEffect(() => {
    if (clinicId && patient?.id) {
      getAppointments();
    }
  }, [clinicId, patient?.id]);

  const getAppointments = async () => {
    try {
      setLoading(true);
      const response = await patientService.getPatientAppointments(clinicId, patient.id);
      setAppointmentsData(response?.data || { content: [], totalElements: 0, number: 0, totalPages: 1 });
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Failed to load appointments");
      setAppointmentsData({ content: [], totalElements: 0, number: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getConfirmationStatus = (status) => {
    switch (status) {
      case 1: return { text: "Confirmed", variant: "success", icon: "bi-check-circle" };
      case 2: return { text: "Pending", variant: "warning", icon: "bi-clock" };
      case 3: return { text: "Cancelled", variant: "danger", icon: "bi-x-circle" };
      default: return { text: "Unknown", variant: "secondary", icon: "bi-question-circle" };
    }
  };

  const getReminderIcon = (sentType) => {
    switch (sentType) {
      case 0: return { icon: "bi-envelope", variant: "primary" };
      case 1: return { icon: "bi-chat-dots", variant: "info" };
      case 2: return { icon: "bi-telephone", variant: "success" };
      case 3: return { icon: "bi-check-circle", variant: "success" };
      default: return { icon: "bi-circle", variant: "secondary" };
    }
  };

  // *** SAFE DESTRUCTURING ***
  const appointments = appointmentsData?.content || [];
  const totalElements = appointmentsData?.totalElements || 0;
  const currentPage = appointmentsData?.number || 0;
  const totalPages = appointmentsData?.totalPages || 1;

  if (!appointmentsData || loading) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-bottom-0 py-3">
          <h5 className="card-title mb-0">
            <i className="bi bi-calendar-check me-2 text-primary"></i>
            Appointments
          </h5>
        </div>
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white border-bottom-0 py-3">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="bi bi-calendar-check me-2 text-primary"></i>
            Appointments{" "}
            <span className="badge bg-primary text-white ms-2">{totalElements}</span>
          </h5>

        
        </div>
      </div>

      <div className="card-body p-0">
        {appointments.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-calendar-x display-4 text-muted"></i>
            <p className="text-muted mt-3 mb-0">No appointments found</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="ps-4">Date & Time</th>
                  <th>Doctor</th>
                  <th>Patient</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Reminders</th>
                 
                </tr>
              </thead>

              <tbody>
                {appointments.map((appointment) => {
                  const status = getConfirmationStatus(appointment?.isConfirmed);
                  const apptDate = new Date(appointment?.dateAppointment);
                  const isToday = new Date().toDateString() === apptDate.toDateString();
                  const doctor = appointment?.doctor || {};
                  const pat = appointment?.patient || {};
                  const statusObj = appointment?.appointmentStatus || {};

                  return (
                    <tr key={appointment.id}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          {isToday && <span className="badge bg-warning me-2">Today</span>}
                          <div>
                            <div className="fw-semibold">{formatDate(appointment.dateAppointment)}</div>
                            <small className="text-muted">
                              <i className="bi bi-clock me-1"></i>
                              {appointment.startTime} - {appointment.endTime}
                            </small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="fw-medium">
                          Dr. {doctor.firstName} {doctor.lastName}
                        </div>
                      </td>

                      <td>
                        <div className="fw-medium">
                          {pat.firstName} {pat.lastName}
                          {pat.preferredName && <small className="text-muted ms-1">({pat.preferredName})</small>}
                        </div>
                        <small className="text-muted">
                          <i className="bi bi-telephone me-1"></i> {pat.phoneNumber}
                        </small>
                      </td>

                      <td><span className="text-muted">15 mins</span></td>

                      <td>
                        <span className={`badge bg-${status.variant}`}>
                          <i className={`bi ${status.icon} me-1`}></i>
                          {status.text}
                        </span>
                      </td>

                      <td>
                        <div className="d-flex gap-2">
                          {statusObj.is_prebooked && (
                            <i className={`bi ${getReminderIcon(statusObj.prebooked_sent_type).icon} text-${getReminderIcon(statusObj.prebooked_sent_type).variant}`} />
                          )}
                          {statusObj.is_first && (
                            <i className={`bi ${getReminderIcon(statusObj.first_sent_type).icon} text-${getReminderIcon(statusObj.first_sent_type).variant}`} />
                          )}
                          {statusObj.is_second && (
                            <i className={`bi ${getReminderIcon(statusObj.second_sent_type).icon} text-${getReminderIcon(statusObj.second_sent_type).variant}`} />
                          )}
                          {statusObj.is_last && (
                            <i className={`bi ${getReminderIcon(statusObj.last_sent_type).icon} text-${getReminderIcon(statusObj.last_sent_type).variant}`} />
                          )}
                          {statusObj.is_same_day && (
                            <i className={`bi ${getReminderIcon(statusObj.same_day_sent_type).icon} text-${getReminderIcon(statusObj.same_day_sent_type).variant}`} />
                          )}
                        </div>
                      </td>

                    
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="card-footer bg-white border-top-0">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted">
              Showing {appointments.length} of {totalElements} appointments
            </div>

            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => getAppointments(currentPage - 1)}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>

                {[...Array(totalPages)].map((_, index) => (
                  <li key={index} className={`page-item ${currentPage === index ? "active" : ""}`}>
                    <button className="page-link" onClick={() => getAppointments(index)}>
                      {index + 1}
                    </button>
                  </li>
                ))}

                <li className={`page-item ${currentPage === totalPages - 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => getAppointments(currentPage + 1)}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientAppointments;
